import { useState } from 'react';
import { motion } from 'framer-motion';
import { Building2, Globe, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * StartChoiceModal - Modal for new users to choose their path
 * 
 * Options:
 * 1. Build site in ApisAI (create kennel/litter/stud)
 * 2. Connect existing website (URL scan + widget)
 */
export default function StartChoiceModal({ isOpen, onClose, onChoice }) {
  const [selectedOption, setSelectedOption] = useState(null);

  if (!isOpen) return null;

  const handleContinue = () => {
    if (selectedOption) {
      onChoice?.(selectedOption);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-2xl p-6 sm:p-8 max-w-2xl w-full shadow-2xl"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-[#F47A3B] flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">
            Jak chcesz zacząć?
          </h1>
          <p className="text-zinc-500">
            Wybierz opcję, która najlepiej pasuje do Twojej sytuacji
          </p>
        </div>

        {/* Options */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {/* Option 1: Build in ApisAI */}
          <button
            onClick={() => setSelectedOption('build')}
            className={`p-6 rounded-2xl border-2 text-left transition-all ${
              selectedOption === 'build'
                ? 'border-[#F47A3B] bg-[#F47A3B]/5'
                : 'border-zinc-200 hover:border-zinc-300'
            }`}
          >
            <div className={`w-12 h-12 rounded-xl mb-4 flex items-center justify-center ${
              selectedOption === 'build' ? 'bg-[#F47A3B]' : 'bg-zinc-100'
            }`}>
              <Building2 className={`w-6 h-6 ${selectedOption === 'build' ? 'text-white' : 'text-zinc-500'}`} />
            </div>
            <h3 className="font-semibold text-zinc-900 mb-1">Zbuduj stronę w ApisAI</h3>
            <p className="text-sm text-zinc-500">
              Stwórz profesjonalną stronę hodowli, miotu lub reproduktora w kilka minut
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-600">Szybki start</span>
              <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-600">Darmowe</span>
            </div>
          </button>

          {/* Option 2: Connect existing */}
          <button
            onClick={() => setSelectedOption('connect')}
            className={`p-6 rounded-2xl border-2 text-left transition-all ${
              selectedOption === 'connect'
                ? 'border-[#F47A3B] bg-[#F47A3B]/5'
                : 'border-zinc-200 hover:border-zinc-300'
            }`}
          >
            <div className={`w-12 h-12 rounded-xl mb-4 flex items-center justify-center ${
              selectedOption === 'connect' ? 'bg-[#F47A3B]' : 'bg-zinc-100'
            }`}>
              <Globe className={`w-6 h-6 ${selectedOption === 'connect' ? 'text-white' : 'text-zinc-500'}`} />
            </div>
            <h3 className="font-semibold text-zinc-900 mb-1">Mam już swoją stronę</h3>
            <p className="text-sm text-zinc-500">
              Połącz istniejącą stronę i zwiększ jej widoczność w AI
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-600">AI Scan</span>
              <span className="text-xs px-2 py-1 rounded-full bg-orange-50 text-[#F47A3B]">Widget</span>
            </div>
          </button>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-zinc-200"
          >
            Później
          </Button>
          <Button
            onClick={handleContinue}
            disabled={!selectedOption}
            className={`flex-1 ${
              selectedOption 
                ? 'bg-[#F47A3B] hover:bg-[#E06A2B] text-white' 
                : 'bg-zinc-200 text-zinc-500'
            }`}
          >
            Kontynuuj
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Note */}
        <p className="text-center text-xs text-zinc-400 mt-4">
          Możesz zmienić zdanie w każdej chwili
        </p>
      </motion.div>
    </div>
  );
}
